import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useAuth } from "@/context/AuthContext";

/**
 * RealtimeContext — one WebSocket per browser tab.
 *
 * Backend broadcasts `{channel, action}` signals whenever a mutation lands
 * on that channel's REST prefix. Pages register a callback via
 * `useRealtimeChannel(channel, cb)` and re-fetch their data when the signal
 * fires. No delta merging — dead simple, no partial-state bugs.
 */

const RealtimeCtx = createContext(null);

const BACKEND_URL =
    process.env.REACT_APP_BACKEND_URL || window.location.origin;

// Convert https://x.com or http://x.com → wss://x.com/api/ws (or ws://).
// Auth travels via the `access_token` httpOnly cookie which the browser
// attaches to the WebSocket handshake automatically on same-origin.
function buildWsUrl() {
    const url = new URL(BACKEND_URL);
    url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
    url.pathname = "/api/ws";
    return url.toString();
}

export function RealtimeProvider({ children }) {
    const { user } = useAuth();
    const [status, setStatus] = useState("offline"); // offline | connecting | online
    const [connections, setConnections] = useState(0);
    const wsRef = useRef(null);
    const listenersRef = useRef(new Set()); // {channel, cb}
    const reconnectTimerRef = useRef(null);
    const pingTimerRef = useRef(null);
    const attemptRef = useRef(0);
    const closedByUserRef = useRef(false);

    const clearTimers = useCallback(() => {
        if (reconnectTimerRef.current) {
            clearTimeout(reconnectTimerRef.current);
            reconnectTimerRef.current = null;
        }
        if (pingTimerRef.current) {
            clearInterval(pingTimerRef.current);
            pingTimerRef.current = null;
        }
    }, []);

    const connect = useCallback(() => {
        if (wsRef.current) return; // already have one

        closedByUserRef.current = false;
        setStatus("connecting");
        let ws;
        try {
            ws = new WebSocket(buildWsUrl());
        } catch (e) {
            console.warn("[Realtime] WebSocket ctor threw:", e?.message || e);
            setStatus("offline");
            scheduleReconnect();
            return;
        }
        wsRef.current = ws;

        ws.onopen = () => {
            attemptRef.current = 0;
            setStatus("online");
            // Client-side keep-alive: send ping every 25s so intermediary
            // proxies don't idle-drop the socket.
            pingTimerRef.current = setInterval(() => {
                try {
                    ws.readyState === WebSocket.OPEN && ws.send("ping");
                } catch (err) {
                    console.warn("[Realtime] ping send failed:", err?.message || err);
                }
            }, 25000);
        };

        ws.onmessage = (ev) => {
            let msg;
            try {
                msg = JSON.parse(ev.data);
            } catch (err) {
                // Bad frame from the server — log so we can spot corruption.
                console.warn("[Realtime] non-JSON frame:", err?.message || err);
                return;
            }
            if (msg.type === "hello") {
                if (typeof msg.connections === "number")
                    setConnections(msg.connections);
                return;
            }
            if (msg.type === "event") {
                for (const entry of listenersRef.current) {
                    if (entry.channel === msg.channel || entry.channel === "*") {
                        try {
                            entry.cb(msg);
                        } catch (err) {
                            // Listener callback threw — surface it so
                            // consumer bugs don't hide behind the WS layer.
                            console.error(
                                `[Realtime] listener for "${entry.channel}" threw`,
                                err
                            );
                        }
                    }
                }
            }
        };

        ws.onerror = () => {
            // onclose will fire; nothing to do here.
        };

        ws.onclose = () => {
            wsRef.current = null;
            clearTimers();
            if (closedByUserRef.current) {
                setStatus("offline");
                return;
            }
            setStatus("offline");
            scheduleReconnect();
        };
    }, [clearTimers]);

    const scheduleReconnect = useCallback(() => {
        if (reconnectTimerRef.current) return;
        // Exponential backoff, capped at 15s.
        const attempt = Math.min(attemptRef.current, 6);
        const delay = Math.min(15000, 500 * Math.pow(2, attempt));
        attemptRef.current += 1;
        reconnectTimerRef.current = setTimeout(() => {
            reconnectTimerRef.current = null;
            connect();
        }, delay);
    }, [connect]);

    const disconnect = useCallback(() => {
        closedByUserRef.current = true;
        clearTimers();
        if (wsRef.current) {
            try {
                wsRef.current.close();
            } catch (err) {
                console.warn("[Realtime] close threw:", err?.message || err);
            }
            wsRef.current = null;
        }
        setStatus("offline");
    }, [clearTimers]);

    // Auto-connect on login, teardown on logout.
    useEffect(() => {
        if (user) {
            connect();
        } else {
            disconnect();
        }
        return () => disconnect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    // If the browser regains focus after long sleep and we're offline, retry now.
    useEffect(() => {
        const onVisible = () => {
            if (document.visibilityState === "visible" && !wsRef.current && user) {
                attemptRef.current = 0;
                connect();
            }
        };
        document.addEventListener("visibilitychange", onVisible);
        return () => document.removeEventListener("visibilitychange", onVisible);
    }, [connect, user]);

    const subscribe = useCallback((channel, cb) => {
        const entry = { channel, cb };
        listenersRef.current.add(entry);
        return () => listenersRef.current.delete(entry);
    }, []);

    const value = useMemo(
        () => ({ status, connections, subscribe }),
        [status, connections, subscribe]
    );

    return <RealtimeCtx.Provider value={value}>{children}</RealtimeCtx.Provider>;
}

export function useRealtime() {
    return useContext(RealtimeCtx);
}

/**
 * useRealtimeChannel(channel, callback)
 * Re-runs `callback` every time the backend broadcasts a change to `channel`.
 * The callback should re-fetch the affected data; do NOT mutate state directly
 * from the WS payload (server only sends signals, not payloads).
 */
export function useRealtimeChannel(channel, callback) {
    const rt = useContext(RealtimeCtx);
    const cbRef = useRef(callback);
    cbRef.current = callback;
    useEffect(() => {
        if (!rt) return undefined;
        return rt.subscribe(channel, (msg) => cbRef.current && cbRef.current(msg));
    }, [rt, channel]);
}
